import type { FormInstance } from "@payping/web-components/legacyComponents/Form/types";
import { Dispatch, FC, SetStateAction, useState } from "react";
import {
  Button,
  Col,
  Flex,
  Input,
  InputNumber,
  LegacyForm,
  Row,
  message,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import { isAlphabet } from "@payping/utils/string";
import { CuToman, FiChevronDown, FiChevronUp } from "@payping/icons";
import { settlementApis } from "@payping/api";
import { setCashOutRequestList } from "@payping/redux/actions";
import { ValidMobileNumber, ValidNationalId, ValidSheba } from "@payping/utils/validation";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { tableAddRowButtonId } from "@/assets/googleTrackIds";
import { SettlementRowType } from "./secondStep";
import { numberFormatter } from "@payping/utilities-shared/number";

interface Props {
  form: FormInstance<SettlementRowType>;
  setOpenDrawer?: Dispatch<SetStateAction<boolean>>;
  loadingList: boolean;
}
const ManualAddition: FC<Props> = ({ form, setOpenDrawer, loadingList }: Props) => {
  const { t } = useAppTranslation();
  const { createdRequest, overview } = useAppSelector((state) => state.cashOuts);
  const dispatch = useAppDispatch();
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const { breakpoints } = usePaypingContext();
  const { lg, sm } = breakpoints;

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (createdRequestParam) => {
      if (!form.getFieldValue("iban") && !form.getFieldValue("amount")) return;
      setIsSubmitClicked(true);
      await form.validateFields();
      setIsSubmitClicked(false);
      if (createdRequestParam?.code) {
        const iban = `IR${form.getFieldValue("iban")}`;
        const amount = Number(form.getFieldValue("amount"));
        const description = form.getFieldValue("description");
        const mobile = form.getFieldValue("mobile");
        const nationalId = form.getFieldValue("nationalId");

        try {
          await settlementApis.createSettlementWithRequestCodeWithModelPost(
            createdRequestParam?.code,
            {
              amount,
              description,
              iban: iban.toLocaleLowerCase().replace("ir", ""),
              mobile,
              nationalId,
            },
            { isRaw: true },
          );
        } catch (error: any) {
          const contentType = error.headers.get("content-type");
          const errorMsg = contentType.includes("application/json") ? await error.json() : "";
          message.error({ content: errorMsg.error, duration: 8 });
        }
        const response = await settlementApis.settlementsListWithRequestCodeWithQueryWithGetAllGet(
          createdRequestParam?.code,
          undefined,
          undefined,
          undefined,
          undefined,
          true,
        );
        dispatch(setCashOutRequestList({ ...createdRequestParam, settlements: response.data }));
        form.resetFields(["amount", "iban", "description", "mobile", "nationalId"]);
      }
      setOpenDrawer?.(false);
    },
    [dispatch, form, setOpenDrawer],
    undefined,
    { useProgress: true },
  );

  const shabaValidation = (value: string) => {
    const pureValue = value.toLocaleLowerCase().replace("ir", "");
    return ValidSheba(pureValue);
  };

  const conditionalRequired = createdRequest?.settlements ? createdRequest.settlements?.length < 1 : true;

  return (
    <Row gutter={[8, 32]}>
      {/* < Shaba > */}
      <Col xs={24} lg={12}>
        <LegacyForm.Item
          className={"mb-0"}
          label={t("shaba")}
          name="iban"
          dependencies={["iban", "amount"]}
          layout={"vertical"}
          rules={[
            {
              required: conditionalRequired,
            },
            {
              validator(_, value) {
                if (isSubmitClicked && value && shabaValidation(value)) {
                  return Promise.reject(new Error(t("settlement.error.shabaValidation")));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder={t("settlement.create.shebaInput")}
            onPressEnter={(event) => {
              event.preventDefault();
            }}
          />
        </LegacyForm.Item>
      </Col>

      {/* < Amount > */}
      <Col xs={24} lg={12}>
        <LegacyForm.Item
          className={"tw:mb-0!"}
          name={"amount"}
          dependencies={["iban", "amount"]}
          layout={"vertical"}
          label={t("common.amount")}
          rules={[
            {
              required: conditionalRequired,
            },
          ]}
        >
          <InputNumber
            formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
            parser={(value) => numberFormatter.parse(value)}
            className="tw:w-full!"
            suffix={<CuToman width={24} height={24} />}
            inputMode="numeric"
            changeOnWheel={false}
            controls={false}
            placeholder={t("settlement.create.amountInput")}
            onPressEnter={(event) => {
              event.preventDefault();
            }}
          />
        </LegacyForm.Item>
      </Col>

      {showMore || !lg ? (
        <>
          {/* < Phone > */}
          <Col xs={24} lg={12}>
            <LegacyForm.Item noStyle dependencies={["iban", "amount"]}>
              {({ getFieldValue }) => (
                <LegacyForm.Item
                  className={"tw:mb-0!"}
                  label={t("settlement.phone")}
                  layout={"vertical"}
                  rules={[
                    {
                      required:
                        overview?.isCritical && isSubmitClicked && (getFieldValue("iban") || getFieldValue("amount")),
                    },
                    () => ({
                      validator(_, value) {
                        if (value && !ValidMobileNumber(value)) {
                          return Promise.reject(new Error(t("settlement.id.validPhoneNumber")));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  name={"mobile"}
                >
                  <Input maxLength={11} placeholder={t("mobile")} />
                </LegacyForm.Item>
              )}
            </LegacyForm.Item>
          </Col>

          {/* < NationalId > */}
          <Col xs={24} lg={12}>
            <LegacyForm.Item noStyle dependencies={["iban", "amount"]}>
              {({ getFieldValue }) => (
                <LegacyForm.Item
                  className={"mb-0"}
                  label={t("settlement.nationalId")}
                  layout={"vertical"}
                  rules={[
                    {
                      required:
                        overview?.isCritical && isSubmitClicked && (getFieldValue("iban") || getFieldValue("amount")),
                    },
                    () => ({
                      validator(_, value) {
                        if (value && !ValidNationalId(value)) {
                          return Promise.reject(new Error(t("error.nationalId")));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  name={"nationalId"}
                >
                  <Input placeholder={t("nationalId")} />
                </LegacyForm.Item>
              )}
            </LegacyForm.Item>
          </Col>

          {/* < Description > */}
          <Col xs={24} lg={24}>
            <LegacyForm.Item
              className={"tw:mb-0!"}
              name={"description"}
              layout={"vertical"}
              label={t("description")}
              rules={[
                {
                  validator(_, value) {
                    if (!isAlphabet(value, { withNumber: true }))
                      return Promise.reject(new Error(t("settlement.error.descriptionException")));
                    if (value && value.length > 30) return Promise.reject(new Error(t("settlement.error.description")));
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.TextArea placeholder={t("settlement.create.descriptionInput")} className="tw:leading-8" />
            </LegacyForm.Item>
          </Col>
        </>
      ) : null}

      <Col xs={24} lg={12}>
        {lg ? (
          <Flex className="tw:h-full" gap={8} align={"center"} justify={"start"}>
            <Button
              loading={loadingSubmit || loadingList}
              type={"primary"}
              onClick={() => onSubmit(createdRequest)}
              data-google-track={tableAddRowButtonId}
            >
              {t("add.add")}
            </Button>

            <Button
              type="link"
              icon={showMore ? <FiChevronUp width={14} height={14} /> : <FiChevronDown width={14} height={14} />}
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? t("common.less") : t("common.more")}
            </Button>
          </Flex>
        ) : (
          <Row gutter={[8, 8]}>
            <Col xs={12} sm={18} md={8}>
              <div className={sm ? "me-2 mb-0" : "me-0 mb-1"}>
                <Button
                  loading={loadingSubmit}
                  type={"primary"}
                  onClick={() => onSubmit(createdRequest)}
                  block
                  data-google-track={tableAddRowButtonId}
                >
                  {t("add.add")}
                </Button>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Button
                block
                onClick={() => {
                  form.resetFields(["iban", "amount", "mobile", "nationalId", "description"]);
                  setOpenDrawer?.(false);
                }}
              >
                {t("cancel")}
              </Button>
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
};

export default ManualAddition;
